/* eslint no-console: 0 */
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { useGetCurrentUser } from '../../../hooks/useGetCurrentUser.ts';
import { useUpdateUserNameMutation } from '../../../hooks/useUpdateUserNameMutation.ts';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';
import TextField from '../../bits/TextField.jsx';
import Loading from '../Loading.jsx';

const Alert: React.FC<any> = props => (
  <MuiAlert elevation={6} variant="filled" {...props} />
);

const EditName: React.FC = () => {
  const { data: user, isLoading } = useGetCurrentUser();
  const mutation = useUpdateUserNameMutation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const history = useHistory();
  const queryClient = useQueryClient();
  const isMounted = useRef(true);

  useSetModalTitle('Name');

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
  }, [user?.firstName, user?.lastName]);

  const normalizedFirstName = firstName.trim();
  const normalizedLastName = lastName.trim();
  const canUpdate = useMemo(() => {
    if (!normalizedFirstName || !normalizedLastName) {
      return false;
    }

    return normalizedFirstName !== user?.firstName || normalizedLastName !== user?.lastName;
  }, [normalizedFirstName, normalizedLastName, user?.firstName, user?.lastName]);

  const onSave = useCallback(async () => {
    setIsUpdating(true);

    try {
      await mutation.mutateAsync({
        firstName: normalizedFirstName,
        lastName: normalizedLastName,
      });
      await queryClient.invalidateQueries({
        queryKey: ['useGetCurrentUser'],
        refetchType: 'active',
      });

      if (isMounted.current) {
        screenNavigator.exitCurrentScreen(history);
      }
    } catch (error) {
      console.error('Failed to update name', error);
      if (isMounted.current) {
        setErrorMessage('Failed to update name');
      }
    } finally {
      if (isMounted.current) {
        setIsUpdating(false);
      }
    }
  }, [history, mutation, normalizedFirstName, normalizedLastName, queryClient]);

  const onHideErrorBar = useCallback(() => setErrorMessage(null), []);

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && user && (
        <>
          <TextField
            autoFocus
            id="firstName"
            type="text"
            label="First name"
            value={firstName}
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 255 }}
            disabled={isUpdating}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
          />
          <TextField
            id="lastName"
            type="text"
            label="Last name"
            value={lastName}
            fullWidth
            margin="normal"
            inputProps={{ maxLength: 255 }}
            disabled={isUpdating}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLastName(event.target.value)}
          />

          <div style={{ marginTop: '30px' }}>
            <Button
              onClick={onSave}
              color="primary"
              disabled={!canUpdate || isUpdating}
              loading={isUpdating}
            >Update</Button>
          </div>
        </>
      )}

      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={onHideErrorBar}
      >
        <Alert onClose={onHideErrorBar} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export { EditName };
